import { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Textarea,
  Badge,
  Select,
  SelectContent,
  SelectItem
} from "./UI";
import { Building2, Upload, Check, Users, Info } from "lucide-react";
import { SettingsAPI } from "api/settings/service";
import { useNavigate } from "react-router-dom";
import { useAlert } from "components/UI_components/Alert";
import { INDIAN_STATES } from "utils/utilityFunctions";

const mapCompanyData = (data) => {
  const rawContact = data?.contact_number || "";
  return {
    name: data.organization_name,
    address: data.business_address,
    gst: data?.gst_number,
    registration: data?.cin,
    contact_number: rawContact.startsWith("+91") ? rawContact.slice(3) : rawContact,
    logo: data?.logo_image_file,
    pincode: data?.pincode,
    state: data?.state,
    website: data?.website,
  };
};

export function CompanySection() {
  const [company, setCompany] = useState({});
  const { addAlert } = useAlert();
  const [isUploading, setIsUploading] = useState(false);
  const [isEnablingAI, setIsEnablingAI] = useState(false);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const [aiServiceStatus, setAiServiceStatus] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;

    const fetchOrganizationData = async () => {
      const res = await SettingsAPI.getOrganization(orgId);
      setCompany(mapCompanyData(res.data));
    };

    const fetchAIServiceStatus = async () => {
      try {
        const res = await SettingsAPI.getAIServiceStatus(orgId);
        setAiServiceStatus(res.data);
      } catch (_) { }
    };

    const fetchLastSyncStatus = async () => {
      try {
        const res = await SettingsAPI.getLastSyncStatus(orgId);
        setLastSyncStatus(res.data);
      } catch (_) { }
    };

    fetchOrganizationData();
    fetchAIServiceStatus();
    fetchLastSyncStatus();
  }, []);

  const handleUpdate = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;
    const payload = {
      organization_name: company.name,
      business_address: company.address,
      gst_number: company.gst,
      cin: company.registration,
      contact_number: company.contact_number ? `+91${company.contact_number}` : "",
      pincode: company.pincode ? parseInt(company.pincode) : null,
      state: company.state,
      website: company.website,
      // logo_image_file: company.logo,
    }
    await SettingsAPI.updateOrganization(orgId, payload);
    addAlert("Company settings updated", "success");
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;

    const formData = new FormData();
    formData.append("logo_image_file", file);

    try {
      setIsUploading(true);
      const res = await SettingsAPI.updateOrganization(orgId, formData);
      setCompany(prev => ({ ...prev, logo: res.data.logo_image_file }));
      addAlert("Your company logo has been updated successfully.", "success");
    } catch (error) {
      addAlert("Failed to upload logo. Please try again.", "destructive");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    handleUpdate();
  };

  const handleEnableAIServices = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) {
      addAlert("Organization not found.", "destructive");
      return;
    }

    try {
      setIsEnablingAI(true);
      await SettingsAPI.createOrganizationSearchIndex({ organization_id: orgId });
      addAlert("SiteMaster AI services enabled successfully.", "success");
    } catch (error) {
      addAlert(error?.message || "Failed to enable SiteMaster AI services.", "destructive");
    } finally {
      setIsEnablingAI(false);
    }
  };

  const handleSyncOrganizationData = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) {
      addAlert("Organization not found.", "destructive");
      return;
    }

    try {
      setIsSyncingData(true);
      await SettingsAPI.pushOrganizationSearchData({ organization_id: orgId });
      addAlert("Organization data sync started successfully.", "success");
    } catch (error) {
      addAlert(error?.message || "Failed to sync organization data.", "destructive");
    } finally {
      setIsSyncingData(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-primary-text">Company</h2>
          <p className="text-text-gray mt-1">Manage your organization's details.</p>
        </div>
        <Badge variant="primary" className="bg-sm-primary/10 text-sm-primary border-0">
          <Building2 className="h-3 w-3 mr-1" />
          Owner / Admin Only
        </Badge>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Company Logo</CardTitle>
          <CardDescription>Your logo appears on invoices and reports.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {
              company?.logo ? (
                <img src={company?.logo} alt="Logo" className="w-24 h-24 rounded-xl border border-sm-border/50" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-sm-primary to-sm-secondary flex items-center justify-center shadow-inner">
                  <span className="text-white font-bold text-2xl">SM</span>
                </div>
              )
            }
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleLogoClick}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4" />
                {isUploading ? "Uploading..." : company?.logo ? "Change Logo" : "Upload Logo"}
              </Button>
              <p className="text-xs text-text-gray">PNG or SVG. Recommended: 200x200px.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Business Information</CardTitle>
          <CardDescription>Official details for invoices and compliance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={company.name}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Registered Address</Label>
            <Textarea
              id="address"
              value={company.address}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
              className="resize-none"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                value={company.pincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCompany({ ...company, pincode: val });
                }}
                placeholder="6-digit pincode"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select
                value={company.state}
                onValueChange={(val) => setCompany({ ...company, state: val })}
              >
                <SelectContent>
                  {INDIAN_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_number">Contact Number</Label>
              <div className="flex">
                <div className="flex h-10 items-center justify-center rounded-l-lg border border-r-0 border-sm-border bg-gray-bg/50 px-3 text-sm text-text-gray font-medium">
                  +91
                </div>
                <Input
                  id="contact_number"
                  value={company.contact_number}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setCompany({ ...company, contact_number: val });
                  }}
                  className="rounded-l-none"
                  placeholder="10-digit number"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={company.website || ""}
                onChange={(e) => setCompany({ ...company, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gst">GST Number</Label>
              <Input
                id="gst"
                value={company.gst}
                onChange={(e) => setCompany({ ...company, gst: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration">Registration / CIN</Label>
              <Input
                id="registration"
                value={company.registration}
                onChange={(e) => setCompany({ ...company, registration: e.target.value })}
              />
            </div>
          </div>

          {/* <div className="pt-2 border-t border-sm-border/50">
            <Label className="mb-2 block">SiteMaster AI Services</Label>
            <p className="text-sm text-text-gray mb-3">
              Enable AI services and sync your organization data to search.
            </p>

            <div className="flex flex-wrap gap-3 mb-3">
              {aiServiceStatus && (
                <div className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border"
                  style={{
                    background: aiServiceStatus.ai_services_enabled ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
                    borderColor: aiServiceStatus.ai_services_enabled ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)",
                    color: aiServiceStatus.ai_services_enabled ? "#16a34a" : "#dc2626",
                  }}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: aiServiceStatus.ai_services_enabled ? "#22c55e" : "#ef4444" }}
                  />
                  AI Services: {aiServiceStatus.ai_services_enabled ? "Enabled" : "Disabled"}
                </div>
              )}

              {lastSyncStatus && (
                <div className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border"
                  style={{
                    background: lastSyncStatus.is_synced ? "rgba(59,130,246,0.08)" : "rgba(234,179,8,0.08)",
                    borderColor: lastSyncStatus.is_synced ? "rgba(59,130,246,0.3)" : "rgba(234,179,8,0.3)",
                    color: lastSyncStatus.is_synced ? "#2563eb" : "#ca8a04",
                  }}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: lastSyncStatus.is_synced ? "#3b82f6" : "#eab308" }}
                  />
                  Last Sync:{" "}
                  {lastSyncStatus.last_synced_at
                    ? new Date(lastSyncStatus.last_synced_at).toLocaleString()
                    : "Never"}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
             {!aiServiceStatus?.ai_services_enabled && (
                <Button
                  variant="outline"
                  onClick={handleEnableAIServices}
                  disabled={isEnablingAI || isSyncingData}
                >
                  {isEnablingAI ? "Enabling..." : "Enable SiteMaster AI Services"}
                </Button>
               )} 
              <Button
                variant="outline"
                onClick={handleSyncOrganizationData}
                disabled={isSyncingData || isEnablingAI}
              >
                {isSyncingData ? "Syncing..." : "Sync Data"}
              </Button>
            </div>
          </div> */}
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-sm-primary" />
            Team & Roles
          </CardTitle>
          <CardDescription>Manage who can access what.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-sm-primary/5 border border-sm-primary/10">
            <Info className="h-5 w-5 text-sm-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary-text">Team management is in Organization settings</p>
              <p className="text-sm text-text-gray mt-1">
                Invite team members, assign roles, and manage permissions from the Organization Team section in the sidebar.
              </p>
              <Button variant="link" className="px-0 h-auto text-sm-primary mt-2" onClick={() => navigate("/dashboard/organization-team")}>
                Go to Organization Team →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Check className="h-4 w-4" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}
